import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaPlay, FaArrowRight, FaArrowLeft } from "react-icons/fa";
import vidlogo from "../Assets/Indorinama-website-elements/Icons/videos.svg";
import axios from "../lib/axios";
import YoutubeModal from "./YoutubeModal";
import { getYoutubeVideoId } from "../utils/youtube";

const Videos = ({ category, embedded = false, configuredVideos, title = "Digital marketing in action", detailed = false, introduction, eyebrow = "Behind the work" }) => {
  const [videos, setVideos] = useState([]);
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(4);

  useEffect(() => {
    const updateItemsPerPage = () => {
      if (window.innerWidth < 640) {
        setItemsPerPage(1);
        return;
      }

      if (window.innerWidth < 1280) {
        setItemsPerPage(2);
        return;
      }

      setItemsPerPage(4);
    };

    updateItemsPerPage();
    window.addEventListener("resize", updateItemsPerPage);

    return () => window.removeEventListener("resize", updateItemsPerPage);
  }, []);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(videos.length / (detailed ? 1 : itemsPerPage)));
    if (currentPage > totalPages - 1) {
      setCurrentPage(0);
    }
  }, [currentPage, itemsPerPage, videos.length, detailed]);

  useEffect(() => {
    if (Array.isArray(configuredVideos)) {
      setVideos(configuredVideos.filter((video) => getYoutubeVideoId(video.youtube_link))
        .slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
      setLoading(false);
      setError(null);
      setCurrentPage(0);
      return;
    }
    const controller = new AbortController();
    const fetchVideos = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/globals/videos', {
          signal: controller.signal,
          params: { _t: Date.now() },
        });
        
        // Get the videos for the current category
        const categoryKey = `${category}`;
        const categoryVideos = Array.isArray(response.data[categoryKey])
          ? response.data[categoryKey].filter((video) => getYoutubeVideoId(video.youtube_link))
          : [];
        
        // Sort videos by sort_order field
        const sortedVideos = [...categoryVideos].sort((a, b) => 
          (a.sort_order || 0) - (b.sort_order || 0)
        );
        
        setVideos(sortedVideos);
        setError(null);
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error('Error fetching videos:', error);
        setError('Failed to load videos');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchVideos();
    return () => controller.abort();
  }, [category, configuredVideos]);

  const pageSize = detailed ? 1 : itemsPerPage;

  const handleNext = () => {
    setCurrentPage((prev) =>
      prev >= Math.ceil(videos.length / pageSize) - 1 ? 0 : prev + 1
    );
  };

  const handlePrev = () => {
    setCurrentPage((prev) =>
      prev === 0 ? Math.ceil(videos.length / pageSize) - 1 : prev - 1
    );
  };

  // Function to extract video ID from YouTube URL
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|shorts\/)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Function to get high-quality thumbnail URL (with fallbacks)
  const getYouTubeThumbnail = (url, quality = "maxresdefault") => {
    const videoId = getYouTubeVideoId(url);
    if (!videoId) return null;
    return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
  };

  const handleThumbnailError = (e, youtubeLink) => {
    const currentSrc = e.target.src || "";
    const videoId = getYouTubeVideoId(youtubeLink);
    if (!videoId) return;

    if (currentSrc.includes("maxresdefault")) {
      e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
      return;
    }

    if (currentSrc.includes("hqdefault")) {
      e.target.src = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    }
  };

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!videos.length) return null;

  const currentVideos = videos.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize
  );

  if (embedded && detailed) {
    const video = currentVideos[0];
    let linkUrl;
    try {
      const url = new URL(video.link_url);
      if (["https:", "http:"].includes(url.protocol)) linkUrl = url.href;
    } catch {
      // Optional links are shown only when they are valid website URLs.
    }
    return (
      <section aria-label={title} className="border-t border-[#161310]/10 bg-[#fcfaf7] py-10 sm:py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-7 max-w-2xl">
            {eyebrow && <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#bc1535]">{eyebrow}</p>}
            <h2 className="dm-serif text-3xl text-[#161310] sm:text-4xl">{title}</h2>
            {introduction && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[#161310]/65">{introduction}</p>}
          </div>
          <article className="overflow-hidden rounded-3xl border border-[#161310]/10 bg-white shadow-sm lg:grid lg:grid-cols-[1.4fr_1fr]">
            <div className="flex items-center bg-[#161310]">
              <iframe key={video.youtube_link}
                src={`https://www.youtube-nocookie.com/embed/${getYoutubeVideoId(video.youtube_link)}?rel=0`}
                title={video.title || `Expertise video ${currentPage + 1}`}
                className="aspect-video w-full border-0"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
            <div className="flex flex-col p-6 sm:p-8">
              {video.category && <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.16em] text-[#bc1535]">{video.category}</p>}
              <h3 className="dm-serif text-2xl text-[#161310] sm:text-3xl">{video.title || "Our work in focus"}</h3>
              {video.description && <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-[#161310]/65">{video.description}</p>}
              {video.highlights?.length > 0 && (
                <ul className="mt-5 space-y-2 text-sm text-[#161310]/75">
                  {video.highlights.filter((item) => item.point).map((item, index) => (
                    <li key={item.id || index} className="flex gap-3"><span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#bc1535]" />{item.point}</li>
                  ))}
                </ul>
              )}
              <div className="mt-auto pt-6">
                <a href={linkUrl || `https://www.youtube.com/watch?v=${getYoutubeVideoId(video.youtube_link)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-bold text-[#bc1535] no-underline hover:underline">
                  {linkUrl ? video.link_label || "Explore this project" : "Watch on YouTube"}<FaArrowRight aria-hidden="true" />
                </a>
              </div>
            </div>
          </article>
          {videos.length > 1 && (
            <div className="mt-5 flex items-center justify-end gap-4">
              <button type="button" onClick={handlePrev} aria-label="Previous expertise video" className="flex h-10 w-10 items-center justify-center rounded-full border border-[#161310]/20 text-[#161310] hover:border-[#bc1535] hover:text-[#bc1535]"><FaArrowLeft /></button>
              <span aria-live="polite" className="text-xs text-[#161310]/60">{currentPage + 1} / {videos.length}</span>
              <button type="button" onClick={handleNext} aria-label="Next expertise video" className="flex h-10 w-10 items-center justify-center rounded-full border border-[#161310]/20 text-[#161310] hover:border-[#bc1535] hover:text-[#bc1535]"><FaArrowRight /></button>
            </div>
          )}
        </div>
      </section>
    );
  }

  if (embedded) {
    const pageCount = Math.ceil(videos.length / pageSize);
    return (
      <section aria-label={title} className="border-b border-[#161310]/10 bg-white py-6 sm:py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-4 flex items-center justify-between gap-4">
            <h2 className="dm-serif text-2xl text-[#161310] sm:text-3xl">{title}</h2>
            {pageCount > 1 && (
              <div className="flex items-center gap-3">
                <button type="button" onClick={handlePrev} aria-label="Previous videos" className="flex h-10 w-10 items-center justify-center rounded-full border border-[#161310]/20 text-[#161310] hover:border-[#bc1535] hover:text-[#bc1535]">
                  <FaArrowLeft />
                </button>
                <span aria-live="polite" className="text-xs text-[#161310]/60">{currentPage + 1} / {pageCount}</span>
                <button type="button" onClick={handleNext} aria-label="Next videos" className="flex h-10 w-10 items-center justify-center rounded-full border border-[#161310]/20 text-[#161310] hover:border-[#bc1535] hover:text-[#bc1535]">
                  <FaArrowRight />
                </button>
              </div>
            )}
          </div>
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${itemsPerPage}, minmax(0, 1fr))` }}>
            {currentVideos.map((video, index) => (
              <div key={video.id || video.youtube_link} className="min-w-0">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${getYoutubeVideoId(video.youtube_link)}?rel=0`}
                  title={video.title || `Digital marketing video ${currentPage * itemsPerPage + index + 1}`}
                  className="aspect-video w-full rounded-xl border-0 bg-black"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
                {video.title && <h3 className="mt-2 text-sm font-semibold text-[#161310]">{video.title}</h3>}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <div className="relative overflow-hidden">
      <YoutubeModal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        videoId={selectedVideoId}
      />
      <div className="mb-8 text-center sm:mb-12">
        <motion.img
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          src={vidlogo}
          alt="Videos" 
          className="mx-auto max-md:mt-8 max-md:h-[3.75rem]"
          style={{ maxHeight: "5rem", filter: "brightness(0) saturate(100%) invert(12%) sepia(75%) saturate(3967%) hue-rotate(341deg) brightness(91%) contrast(97%)" }}
        />
      </div>

      <div className="relative mx-auto max-w-7xl px-4">
        {/* Media row: arrows centered against video cards only (not dots) */}
        <div className="relative">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 xl:gap-6">
            <AnimatePresence mode="wait">
              {currentVideos.map((video, index) => {
                const thumbnailUrl = getYouTubeThumbnail(video.youtube_link);

                return (
                  <motion.div
                    key={`${currentPage}-${index}`}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ delay: index * 0.1 }}
                    className="relative group"
                    onMouseEnter={() => setHoveredVideo(index)}
                    onMouseLeave={() => setHoveredVideo(null)}
                  >
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        const videoId = getYouTubeVideoId(video.youtube_link);
                        setSelectedVideoId(videoId);
                        setModalOpen(true);
                      }}
                      className="block relative no-underline cursor-pointer"
                    >
                      {/* Thumbnail Container */}
                      <div className="relative aspect-video rounded-xl overflow-hidden bg-gray-100">
                        <img
                          src={thumbnailUrl}
                          alt={video.title}
                          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                          onError={(e) => handleThumbnailError(e, video.youtube_link)}
                        />
                        {/* Hover Overlay */}
                        <div
                          className={`absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity duration-300 ${
                            hoveredVideo === index ? "opacity-100" : "opacity-0"
                          }`}
                        >
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: hoveredVideo === index ? 1 : 0 }}
                            className="w-16 h-16 bg-[#bc1535] rounded-full flex items-center justify-center"
                          >
                            <FaPlay className="text-white text-2xl ml-1" />
                          </motion.div>
                        </div>
                      </div>

                      {/* Video Info */}
                      <div className="mt-3">
                        <h3 className="text-base font-semibold text-gray-900 line-clamp-2 mb-1 leading-tight">
                          {video.title}
                        </h3>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {/* Navigation Arrows — centered on the video thumbnails */}
          {videos.length > itemsPerPage && (
            <>
              {/* Mobile Navigation Arrows (below grid) */}
              <div className="mt-6 flex items-center justify-between gap-3 sm:hidden">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  onClick={handlePrev}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-black text-white shadow-lg transition-colors"
                  aria-label="Previous videos"
                >
                  <FaArrowLeft className="text-lg" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  onClick={handleNext}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-black text-white shadow-lg transition-colors"
                  aria-label="Next videos"
                >
                  <FaArrowRight className="text-lg" />
                </motion.button>
              </div>

              {/* Desktop/Tablet Navigation Arrows (centered on video thumbnails) */}
              <div className="hidden sm:block absolute top-0 inset-x-0 pointer-events-none">
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 xl:gap-6">
                  <div className="aspect-video relative" />
                </div>
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-between pointer-events-none">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    onClick={handlePrev}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-black text-white shadow-lg transition-colors pointer-events-auto"
                    aria-label="Previous videos"
                  >
                    <FaArrowLeft className="text-lg" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    onClick={handleNext}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-black text-white shadow-lg transition-colors pointer-events-auto"
                    aria-label="Next videos"
                  >
                    <FaArrowRight className="text-lg" />
                  </motion.button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Video Indicators */}
        {videos.length > itemsPerPage && (
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({
              length: Math.ceil(videos.length / pageSize),
            }).map((_, index) => (
              <motion.button
                key={index}
                whileHover={{ scale: 1.2 }}
                onClick={() => setCurrentPage(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentPage ? "w-6 bg-[#bc1535]" : "bg-gray-300"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Videos;
